import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Lightbulb, MessageSquare, ThumbsUp, Clock, Eye, CheckCircle, 
  XCircle, Send, Trash2, Filter, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FeedbackTab = ({ token }) => {
  const [feedbackList, setFeedbackList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, reviewed, implemented, declined
  const [responseText, setResponseText] = useState({});
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchFeedback();
    fetchStats();
  }, [filter]);

  const fetchFeedback = async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API}/admin/feedback${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedbackList(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/feedback/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const updateStatus = async (feedbackId, newStatus) => {
    setUpdatingId(feedbackId);
    try {
      const adminResponse = responseText[feedbackId] || '';
      await axios.put(
        `${API}/admin/feedback/${feedbackId}/status?status=${newStatus}${adminResponse ? `&admin_response=${encodeURIComponent(adminResponse)}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Status updated to ${newStatus}`);
      setResponseText(prev => ({ ...prev, [feedbackId]: '' }));
      fetchFeedback();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteFeedback = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) return;
    
    try {
      await axios.delete(`${API}/admin/feedback/${feedbackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Feedback deleted');
      fetchFeedback();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete feedback');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock, text: 'Pending' },
      reviewed: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Eye, text: 'Reviewed' },
      implemented: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle, text: 'Implemented' },
      declined: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle, text: 'Declined' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="glass-card border-gray-700">
            <CardContent className="p-4 text-center">
              <BarChart3 className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">{stats.total}</div>
              <div className="text-xs text-gray-500">Total</div>
            </CardContent>
          </Card>
          <Card className="glass-card border-yellow-500/30">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
              <div className="text-2xl font-bold text-yellow-400">{stats.by_status?.pending ?? 0}</div>
              <div className="text-xs text-gray-500">Pending</div>
            </CardContent>
          </Card>
          <Card className="glass-card border-green-500/30">
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <div className="text-2xl font-bold text-green-400">{stats.by_status?.implemented ?? 0}</div>
              <div className="text-xs text-gray-500">Implemented</div>
            </CardContent>
          </Card>
          <Card className="glass-card border-blue-500/30">
            <CardContent className="p-4 text-center">
              <div className="flex justify-center gap-2 mb-2">
                <Lightbulb className="w-5 h-5 text-yellow-400" />
                <MessageSquare className="w-5 h-5 text-blue-400" />
              </div>
              <div className="text-lg font-bold">
                <span className="text-yellow-400">{stats.by_type?.ideas ?? 0}</span>
                <span className="text-gray-500 mx-1">/</span>
                <span className="text-blue-400">{stats.by_type?.feedback ?? 0}</span>
              </div>
              <div className="text-xs text-gray-500">Ideas / Feedback</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter Buttons */}
      <div className="flex flex-wrap gap-2">
        <Filter className="w-4 h-4 text-gray-400 self-center mr-2" />
        {['all', 'pending', 'reviewed', 'implemented', 'declined'].map((f) => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
            className={filter === f ? (
              f === 'pending' ? 'bg-yellow-600' :
              f === 'reviewed' ? 'bg-blue-600' :
              f === 'implemented' ? 'bg-green-600' :
              f === 'declined' ? 'bg-red-600' : 'bg-purple-600'
            ) : ''}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f !== 'all' && stats && (
              <span className="ml-1 opacity-70">({stats.by_status?.[f] ?? 0})</span>
            )}
          </Button>
        ))}
      </div>

      {/* Feedback List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : feedbackList.length === 0 ? (
        <Card className="glass-card border-gray-700">
          <CardContent className="py-12 text-center">
            <Lightbulb className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No {filter !== 'all' ? filter : ''} feedback found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {(Array.isArray(feedbackList) ? feedbackList : []).map((item) => (
            <Card key={item.id} className={`glass-card ${
              item.type === 'idea' ? 'border-yellow-500/30' : 'border-blue-500/30'
            }`}>
              <CardContent className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                      item.type === 'idea' 
                        ? 'bg-yellow-500/20 text-yellow-400' 
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {item.type === 'idea' ? <Lightbulb className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                      {item.type === 'idea' ? 'Challenge Idea' : 'Feedback'}
                    </span>
                    {getStatusBadge(item.status)}
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <ThumbsUp className="w-3 h-3" />
                      {item.upvotes} upvotes
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteFeedback(item.id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                {/* Content */}
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm mb-3 whitespace-pre-wrap">{item.description}</p>

                {/* User Info */}
                <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-800">
                  {item.user_profile_image && (
                    <img src={item.user_profile_image} alt="" className="w-6 h-6 rounded-full" />
                  )}
                  <span className="text-sm text-gray-500">
                    {item.user_display_name} • {formatDate(item.created_at)}
                  </span>
                </div>

                {/* Existing Admin Response */}
                {item.admin_response && (
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 mb-4">
                    <p className="text-xs text-purple-400 font-bold mb-1">Your Response:</p>
                    <p className="text-sm text-gray-300">{item.admin_response}</p>
                  </div>
                )}

                {/* Admin Actions */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Add a response (optional)..."
                      value={responseText[item.id] || ''}
                      onChange={(e) => setResponseText(prev => ({ ...prev, [item.id]: e.target.value }))}
                      className="bg-zinc-800 border-gray-700 text-sm"
                    />
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs text-gray-500 self-center mr-2">Set Status:</span>
                    {item.status !== 'reviewed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(item.id, 'reviewed')}
                        disabled={updatingId === item.id}
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Reviewed
                      </Button>
                    )}
                    {item.status !== 'implemented' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(item.id, 'implemented')}
                        disabled={updatingId === item.id}
                        className="border-green-500/50 text-green-400 hover:bg-green-500/20"
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Implemented
                      </Button>
                    )}
                    {item.status !== 'declined' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(item.id, 'declined')}
                        disabled={updatingId === item.id}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                      >
                        <XCircle className="w-3 h-3 mr-1" />
                        Decline
                      </Button>
                    )}
                    {item.status !== 'pending' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(item.id, 'pending')}
                        disabled={updatingId === item.id}
                        className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20"
                      >
                        <Clock className="w-3 h-3 mr-1" />
                        Reset to Pending
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default FeedbackTab;
